import React, { CSSProperties } from 'react';
import { Features } from '../../../assets/features/Features';
import { useLanguage } from '../../../context/LanguageContext';
import { useTheme } from '../../../context/ThemeContext';
import guilhermeGalvao from '../../../assets/img/doctors/guilherme_galvao.jpeg';

const Doctors: React.FC = () => {
  const { language } = useLanguage();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  // CSS styles
  const specialistCardStyle: CSSProperties = {
    transition: 'all 0.4s ease',
    backgroundColor: isDarkMode ? '#2a2c2e' : '#fff',
    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.05)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  };

  const socialIconStyle: CSSProperties = {
    color: '#1977cc',
    transition: 'all 0.3s ease'
  };

  const badgeStyle: CSSProperties = {
    fontWeight: 500,
    letterSpacing: '0.5px'
  };

  const imageContainerStyle: CSSProperties = {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: '50%',
    margin: '0 auto',
    width: '220px',
    height: '220px'
  };

  const imageStyle: CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.5s ease'
  };

  const sectionHeadingStyle: CSSProperties = {
    color: isDarkMode ? '#ffffff' : '#2c4964',
    fontSize: '1.1rem',
    fontWeight: 600,
    marginBottom: '0.75rem',
    position: 'relative',
    paddingLeft: '1.25rem'
  };

  const sectionContentStyle: CSSProperties = {
    paddingLeft: '1.25rem',
    marginBottom: '1.5rem'
  };

  const textStyle: CSSProperties = {
    color: isDarkMode ? '#f3f4f6' : 'inherit'
  };

  return (
    <section id="specialists" className="doctors section">
      {/* Section Title */}
      <div className="container section-title" data-aos="fade-up">
        <h2>{Features.doctors.title[language]}</h2>
        <p>{Features.doctors.subtitle[language]}</p>
      </div>

      <div className="container">
        <div className="row justify-content-center">
          {/* Dr. Guilherme Galvão */}
          <div className="col-lg-8 col-md-10" data-aos="fade-up" data-aos-delay="100">
            <div className="specialist-card p-4 shadow-sm rounded" style={specialistCardStyle}>
              <div className="text-center mb-4">
                <div style={imageContainerStyle} className="image-hover-container">
                  <img src={guilhermeGalvao} className="img-fluid" alt={Features.doctors.doctorsList.doctor1.name[language]} style={imageStyle} />
                </div>
              </div>
              <div className="specialist-info d-flex flex-column flex-grow-1">
                <h3 className="text-center mb-2" style={isDarkMode ? { color: '#ffffff' } : {}}>{Features.doctors.doctorsList.doctor1.name[language]}</h3>
                <div className="text-center mb-4">
                  <span className="badge bg-primary px-3 py-2 rounded-pill" style={badgeStyle}>{Features.doctors.doctorsList.doctor1.position[language]}</span>
                </div>

                <div className="specialist-section">
                  <h4 style={sectionHeadingStyle} className="section-heading">
                    <i className="bi bi-bookmark-star me-2 text-primary"></i>
                    {language === 'en' ? 'About' : 'Sobre'}
                  </h4>
                  <div style={sectionContentStyle}>
                    <p style={textStyle}>{Features.doctors.doctorsList.doctor1.about[language]}</p>
                  </div>
                </div>

                <div className="specialist-section">
                  <h4 style={sectionHeadingStyle} className="section-heading">
                    <i className="bi bi-award me-2 text-primary"></i>
                    {language === 'en' ? 'Specialties' : 'Especialidades'}
                  </h4>
                  <div style={sectionContentStyle}>
                    <ul className="service-list">
                      {Features.doctors.doctorsList.doctor1.specialties[language].map((specialty, index) => (
                        <li key={index} className="mb-2" style={textStyle}>{specialty}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="specialist-section">
                  <h4 style={sectionHeadingStyle} className="section-heading">
                    <i className="bi bi-mortarboard me-2 text-primary"></i>
                    {language === 'en' ? 'Credentials' : 'Formação'}
                  </h4>
                  <div style={sectionContentStyle}>
                    <ul className="credential-list">
                      {Features.doctors.doctorsList.doctor1.credentials[language].map((credential, index) => (
                        <li key={index} className="mb-2" style={textStyle}>{credential}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-auto">
                  <div className="d-flex justify-content-center social-links">
                    <a href="https://www.facebook.com/clinicaosteopatiapt" target="_blank" rel="noopener noreferrer" className="mx-2 social-icon" style={socialIconStyle}><i className="bi bi-facebook fs-4"></i></a>
                    <a href="https://www.instagram.com/guiosteopata/" target="_blank" rel="noopener noreferrer" className="mx-2 social-icon" style={socialIconStyle}><i className="bi bi-instagram fs-4"></i></a>
                  </div>
                  <div className="text-center mt-2">
                    <a href={`tel:${Features.doctors.doctorsList.doctor1.phone[language].replace(/\s/g, '')}`} className="btn btn-outline-primary rounded-pill px-4 btn-hover-effect">
                      <i className="bi bi-telephone-fill me-2"></i> {Features.doctors.doctorsList.doctor1.phone[language]}
                      <span
                        className="phone-info-icon"
                        data-tooltip-text={language === 'en' ? 'Call to a national landline' : 'Chamada para a rede fixa nacional'}
                      >
                        <i className="bi bi-info-circle-fill"></i>
                      </span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add CSS for hover effects */}
      <style>
        {`
          .specialist-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1) !important;
          }
          .social-icon:hover {
            color: #3291e6 !important;
            transform: translateY(-3px);
          }
          .btn-hover-effect {
            transition: all 0.3s ease;
          }
          .btn-hover-effect:hover {
            transform: scale(1.05);
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
          }
          .image-hover-container:hover img {
            transform: scale(1.05);
          }
          .service-list, .credential-list {
            list-style: none;
            padding-left: 0.5rem;
          }
          .service-list li, .credential-list li {
            position: relative;
            padding-left: 0.5rem;
            transition: all 0.3s ease;
          }
          .service-list li:hover, .credential-list li:hover {
            transform: translateX(5px);
          }
          .section-heading::before {
            content: '';
            position: absolute;
            left: 0;
            top: 50%;
            transform: translateY(-50%);
            width: 4px;
            height: 70%;
            background-color: #1977cc;
            border-radius: 2px;
          }
          .specialist-section {
            margin-bottom: 1.5rem;
            padding-bottom: 0.5rem;
          }
          .dark-theme .specialist-card:hover {
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3) !important;
          }
        `}
      </style>
    </section>
  );
};

export default Doctors;
